import { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { mockTeachers } from '../../mocks/teachers';

const TeacherPage = () => {
  const messageAnimation = useScrollAnimation();
  const teachersAnimation = useScrollAnimation();
  const recruitAnimation = useScrollAnimation();

  useEffect(() => {
    const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
    
    document.title = '岡山市英会話教室の講師紹介｜経験豊富なプロ講師陣｜おかやま英会話教室株式会社';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市の英会話教室の講師をご紹介。経験豊富なプロ講師陣が一人ひとりに合わせた丁寧な指導を行います。キッズからビジネス英語まで幅広く対応。');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '岡山英会話講師,プロ英語教師,経験豊富な講師,ビジネス英語講師,キッズ英語教師');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/teacher`);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/teacher`);
    }
    
    // Add Schema.org JSON-LD for Teacher page
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-page-schema]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": mockTeachers.map((teacher, index) => ({
        "@type": "Person",
        "position": index + 1,
        "name": teacher.name,
        "jobTitle": teacher.role,
        "description": teacher.experience,
        "worksFor": {
          "@type": "EducationalOrganization",
          "name": "おかやま英会話教室株式会社",
          "url": `${siteUrl}/`
        }
      }))
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', 'true');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center mt-20">
        <img
          src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8691fc9fef1272e5a23b582a9f01cf22.jpeg"
          alt="講師紹介"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        
        <div className="relative z-10 text-center text-navy-900 max-w-4xl mx-auto px-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Teacher Profile
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed">
            経験豊富な講師陣があなたの学びをサポート
          </p>
        </div>
      </section>

      {/* Message Section */}
      <section className="py-20 bg-white">
        <div 
          ref={messageAnimation.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-in-up ${messageAnimation.isVisible ? 'visible' : ''}`}
        >
          <span className="bg-mint-500 text-white px-4 py-2 rounded-full text-sm font-medium">
            OUR TEACHERS
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-8">
            経験豊富な講師陣
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            おかやま英会話教室の講師は、全員が英語教育の専門的なトレーニングを受けた
            プロフェッショナルです。海外留学経験や実務経験を持ち、
            それぞれの専門分野で高い指導力を発揮しています。
          </p>
          <p className="text-gray-600 text-lg leading-relaxed">
            一人ひとりの目標や学習スタイルに合わせた丁寧な指導で、
            確実に英語力を伸ばすお手伝いをいたします。
          </p>
        </div>
      </section>

      {/* Teachers Cards Section */}
      <section className="py-20 bg-beige-50">
        <div 
          ref={teachersAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${teachersAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockTeachers.map((teacher) => (
              <article 
                key={teacher.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-full h-80">
                  <img
                    src={teacher.image}
                    alt={teacher.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                
                <div className="p-8">
                  <h3 className="font-bold text-2xl text-navy-900 mb-2">
                    {teacher.name}
                  </h3>
                  <span className="inline-block bg-coral-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                    {teacher.role}
                  </span>
                  
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {teacher.experience}
                  </p>
                  
                  <div className="bg-beige-50 rounded-lg p-4 border-l-4 border-mint-500">
                    <p className="text-gray-600 text-sm leading-relaxed italic">
                      「{teacher.message}」
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Recruitment Section */}
      <section className="py-20 bg-white">
        <div 
          ref={recruitAnimation.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-in-up ${recruitAnimation.isVisible ? 'visible' : ''}`}
        >
          <span className="bg-coral-500 text-white px-4 py-2 rounded-full text-sm font-medium">
            JOIN US
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-8">
            講師募集
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            おかやま英会話教室では、情熱を持って英語教育に取り組んでいただける
            講師を募集しています。経験者はもちろん、未経験の方でも
            充実した研修制度でサポートいたします。
          </p>
          <p className="text-gray-600 text-lg leading-relaxed mb-10">
            子どもから大人まで、幅広い年齢層の生徒さんと関わりながら、
            やりがいのある仕事をしてみませんか？
          </p>
          
          <div className="bg-beige-50 rounded-2xl p-8 mb-10">
            <h3 className="font-bold text-xl text-navy-900 mb-6">募集要項</h3>
            <div className="text-left space-y-4">
              <div className="flex items-start">
                <i className="ri-check-line text-mint-500 text-xl mr-3 mt-1"></i>
                <div>
                  <strong className="text-navy-900">応募資格：</strong>
                  <span className="text-gray-600">英語教育に情熱のある方、TOEIC800点以上または同等の英語力</span>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-line text-mint-500 text-xl mr-3 mt-1"></i>
                <div>
                  <strong className="text-navy-900">勤務形態：</strong>
                  <span className="text-gray-600">正社員・パート・業務委託から選択可能</span>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-line text-mint-500 text-xl mr-3 mt-1"></i>
                <div>
                  <strong className="text-navy-900">待遇：</strong>
                  <span className="text-gray-600">経験・能力に応じて優遇、交通費支給、研修制度完備</span>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-line text-mint-500 text-xl mr-3 mt-1"></i>
                <div>
                  <strong className="text-navy-900">勤務地：</strong>
                  <span className="text-gray-600">岡山市北区○○町1-2-3（オンライン講師も募集中）</span>
                </div>
              </div>
            </div>
          </div>
          
          <a
            href="mailto:recruit@okayama-english.jp"
            className="inline-flex items-center bg-coral-500 hover:bg-coral-600 text-white px-8 py-4 rounded-lg text-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line text-xl mr-3"></i>
            採用情報を見る
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default TeacherPage;